import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { LogOut, ArrowRight, Calculator, CheckCircle2, Circle, X, FileCheck, Boxes, AlertTriangle, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { inrCompact, formatDate } from '../../lib/format';
import EmployeePicker from '../../components/EmployeePicker';

const fmt = (s) => formatDate(s);

const STATUS_COLOR = {
  Notice: 'bg-amber-100 text-amber-700',
  Initiated: 'bg-blue-100 text-blue-700',
  Completed: 'bg-emerald-100 text-emerald-700',
  Absconding: 'bg-rose-100 text-rose-700',
  Cancelled: 'bg-slate-100 text-slate-500',
};

export default function ExitManagement() {
  const [exits, setExits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [empAssets, setEmpAssets] = useState(null);
  const [showInit, setShowInit] = useState(false);
  const [form, setForm] = useState({
    employee_id: '', resignation_date: '', last_working_day: '',
    reason: '', notice_period_days: 60, exit_type: 'Resignation',
    last_day_present: '', absconding_date: '',
  });
  // Absconding — contact log + close
  const [showContact, setShowContact] = useState(false);
  const [contactForm, setContactForm] = useState({ method: 'Phone', outcome: 'No response', notes: '' });
  const [showAbscClose, setShowAbscClose] = useState(false);
  const [abscClose, setAbscClose] = useState({ outcome: 'terminated_abandonment', closed_on: new Date().toISOString().slice(0,10), reason: '' });

  const load = async () => {
    try {
      const [exRes, empRes] = await Promise.all([
        apiGet('/hr/exit'),
        apiGet('/hr/employees'),
      ]);
      setExits(exRes.exits || []);
      setEmployees(empRes.filter(e => e.status !== 'Resigned'));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { load(); }, []);

  const initiate = async (e) => {
    e.preventDefault();
    try {
      if (form.exit_type === 'Absconding') {
        if (!form.last_day_present || !form.absconding_date) {
          toast.error('Last day present and absconding date are required.'); return;
        }
        await apiPost('/hr/exit/absconding/initiate', {
          employee_id: form.employee_id,
          last_day_present: form.last_day_present,
          absconding_date: form.absconding_date,
          reason: form.reason,
        });
      } else {
        await apiPost('/hr/exit/initiate', form);
      }
      toast.success('Exit initiated');
      setShowInit(false);
      setForm({ employee_id: '', resignation_date: '', last_working_day: '', reason: '', notice_period_days: 60, exit_type: 'Resignation', last_day_present: '', absconding_date: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const logContact = async () => {
    if (!selected) return;
    try {
      await apiPost(`/hr/exit/absconding/${selected.id}/contact`, contactForm);
      toast.success('Contact attempt logged');
      setShowContact(false);
      setContactForm({ method: 'Phone', outcome: 'No response', notes: '' });
      openDetail(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const closeAbsconding = async () => {
    if (!selected) return;
    try {
      const res = await apiPost(`/hr/exit/absconding/${selected.id}/close`, abscClose);
      toast.success(`Case closed: ${res.outcome}`);
      setShowAbscClose(false);
      load(); setSelected(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const openDetail = async (exit) => {
    try {
      const res = await apiGet(`/hr/exit/${exit.id}`);
      setSelected(res);
      setEmpAssets(null);
      const eid = res.employee_id || exit.employee_id;
      if (eid) apiGet(`/hr/employees/${eid}/assets`).then(setEmpAssets).catch(() => setEmpAssets(null));
    } catch (err) { toast.error('Failed to load'); }
  };

  const toggleChecklist = async (task_key, is_done) => {
    try {
      await apiPut(`/hr/exit/${selected.id}/checklist`, { task_key, is_done });
      openDetail(selected);
      load();
    } catch (err) { toast.error('Failed'); }
  };

  const calcFnf = async () => {
    try {
      await apiPost(`/hr/exit/${selected.id}/calculate-fnf`);
      toast.success('F&F calculated');
      openDetail(selected);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const complete = async (force = false) => {
    if (!force && !window.confirm('Mark this exit as completed? Employee status will change to Resigned.')) return;
    try {
      await apiPut(`/hr/exit/${selected.id}/complete${force ? '?force=true' : ''}`);
      toast.success('Exit completed');
      setSelected(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'assets_pending_return') {
        if (window.confirm(`${detail.message}\n\nForce-complete and write off the unreturned asset(s) anyway?`)) {
          return complete(true);
        }
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Failed');
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">People & HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Exit & Final Settlement</h1>
          <p className="text-sm text-slate-600 mt-1">Manage resignation, notice period, exit checklist, and auto-compute F&F (salary + leave encashment + gratuity + PF − notice recovery).</p>
        </div>
        <button onClick={() => setShowInit(true)} className="btn-primary flex items-center gap-2" data-testid="initiate-exit-btn">
          <LogOut size={16} /> Initiate Exit
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Employee</th><th>Type</th><th>Resigned On</th><th>Last Working</th><th>Notice</th><th>Status</th><th>F&F</th><th></th>
          </tr></thead>
          <tbody>
            {exits.map(ex => (
              <tr key={ex.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(ex)} data-testid={`exit-row-${ex.id}`}>
                <td>
                  <div className="font-display font-semibold text-[#0F172A]">{ex.employee_name}</div>
                  <div className="text-xs text-slate-500">{ex.emp_code}</div>
                </td>
                <td className="text-xs">{ex.exit_type}</td>
                <td className="text-xs">{fmt(ex.resignation_date)}</td>
                <td className="text-xs">{fmt(ex.last_working_day)}</td>
                <td className="text-xs">{ex.notice_period_days}d</td>
                <td><span className={`tag ${STATUS_COLOR[ex.status] || 'bg-slate-100 text-slate-700'}`}>{ex.status}</span></td>
                <td className="text-xs font-display font-bold">{ex.fnf?.total_payable ? inrCompact(ex.fnf.total_payable) : '—'}</td>
                <td><ArrowRight size={14} className="text-slate-400" /></td>
              </tr>
            ))}
            {!exits.length && <tr><td colSpan="8" className="p-8 text-center text-sm text-slate-500">No exit records yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Initiate modal */}
      {showInit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full">
            <div className="border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Initiate Exit</h2>
              <button onClick={() => setShowInit(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={initiate} className="p-5 space-y-4">
              <div>
                <label className="label">Employee *</label>
                <EmployeePicker
                  employees={employees}
                  value={(employees.find(e => e.id === form.employee_id)?.name) || ''}
                  onChange={(_name, _id, emp) => setForm({ ...form, employee_id: emp?.id || '', notice_period_days: emp?.notice_period_days || form.notice_period_days })}
                  placeholder="Search by name or employee code…"
                  testid="exit-employee-picker"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {form.exit_type !== 'Absconding' && (
                <div>
                  <label className="label">Resignation Date *</label>
                  <input type="date" value={form.resignation_date} onChange={(e) => setForm({ ...form, resignation_date: e.target.value })} className="input" required />
                </div>
                )}
                {form.exit_type !== 'Absconding' && (
                <div>
                  <label className="label">Last Working Day *</label>
                  <input type="date" value={form.last_working_day} onChange={(e) => setForm({ ...form, last_working_day: e.target.value })} className="input" required />
                </div>
                )}
                {form.exit_type === 'Absconding' && (<>
                  <div>
                    <label className="label">Last Day Present *</label>
                    <input type="date" value={form.last_day_present} onChange={(e) => setForm({ ...form, last_day_present: e.target.value })} className="input" required />
                  </div>
                  <div>
                    <label className="label">Absconding From Date *</label>
                    <input type="date" value={form.absconding_date} onChange={(e) => setForm({ ...form, absconding_date: e.target.value })} className="input" required />
                  </div>
                </>)}
                <div>
                  <label className="label">Exit Type</label>
                  <select value={form.exit_type} onChange={(e) => setForm({ ...form, exit_type: e.target.value })} className="input">
                    <option>Resignation</option><option>Termination</option><option>Retirement</option><option>Absconding</option>
                  </select>
                </div>
                {form.exit_type !== 'Absconding' && (
                <div>
                  <label className="label">Notice Period (days)</label>
                  <input type="number" value={form.notice_period_days} onChange={(e) => setForm({ ...form, notice_period_days: parseInt(e.target.value) })} className="input" />
                </div>
                )}
              </div>
              <div>
                <label className="label">Reason</label>
                <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="input" rows={3} />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowInit(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Initiate</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <button className="flex-1 bg-black/40" onClick={() => setSelected(null)} aria-label="Close" />
          <div className="w-full max-w-[680px] bg-slate-50 border-l border-slate-200 flex flex-col">
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-1">Exit · {selected.status}</div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight">{selected.employee_name}</h2>
                <div className="text-sm text-slate-600 mt-0.5">{selected.emp_code} · {selected.exit_type}</div>
              </div>
              <button onClick={() => setSelected(null)} className="w-9 h-9 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-sm"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Dates */}
              <div className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-3 gap-4 text-sm">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Resigned</div><div className="font-display font-bold mt-1">{fmt(selected.resignation_date)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Last Working</div><div className="font-display font-bold mt-1">{fmt(selected.last_working_day)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Notice</div><div className="font-display font-bold mt-1">{selected.notice_period_days} days</div></div>
                {selected.reason && <div className="col-span-3 pt-3 border-t border-slate-100"><div className="text-[10px] uppercase tracking-wider text-slate-500">Reason</div><div className="text-xs text-slate-700 mt-1">{selected.reason}</div></div>}
              </div>

              {/* Checklist */}
              <div className="bg-white border border-slate-200 rounded-sm">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-display font-bold text-base">Exit Checklist</h3>
                  <span className="text-xs text-slate-500">{(selected.checklist || []).filter(c => c.is_done).length} / {(selected.checklist || []).length}</span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {(selected.checklist || []).map(c => (
                    <li key={c.key} className="px-5 py-2.5 flex items-center gap-3" data-testid={`exit-checklist-${c.key}`}>
                      <button onClick={() => toggleChecklist(c.key, !c.is_done)} className={c.is_done ? 'text-emerald-600' : 'text-slate-400 hover:text-[#3b4872]'} disabled={selected.status === 'Completed'}>
                        {c.is_done ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                      </button>
                      <span className={`text-sm flex-1 ${c.is_done ? 'line-through text-slate-500' : 'text-[#0F172A]'}`}>{c.label}</span>
                      {c.done_at && <span className="text-[10px] text-slate-500">{formatDate(c.done_at)}</span>}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Absconding — contact attempts + close */}
              {selected.exit_type === 'Absconding' && selected.status === 'Absconding' && (
                <div className="bg-rose-50 border border-rose-200 rounded-sm p-4 space-y-3" data-testid="absconding-panel">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-sm text-rose-800 flex items-center gap-2"><UserX size={14} />Absconding Case</h3>
                    <div className="flex gap-2">
                      <button onClick={() => setShowContact(true)} className="text-xs px-2 py-1 bg-rose-700 text-white rounded-sm" data-testid="log-contact-btn">Log Contact Attempt</button>
                      <button onClick={() => setShowAbscClose(true)} className="text-xs px-2 py-1 border border-rose-700 text-rose-700 rounded-sm" data-testid="close-case-btn">Close Case</button>
                    </div>
                  </div>
                  <div className="text-xs text-rose-700">Absent since: <strong>{selected.absconding_date}</strong> · Last seen: <strong>{selected.last_day_present}</strong></div>
                  <div className="text-xs font-semibold text-rose-800">Contact attempts ({(selected.contact_attempts || []).length})</div>
                  {(selected.contact_attempts || []).map((c, i) => (
                    <div key={i} className="text-xs bg-white border border-rose-100 rounded-sm px-3 py-2">
                      <span className="font-semibold">{c.method}</span>: {c.outcome} · {c.notes || ''} <span className="text-slate-400 ml-1">{c.logged_at?.slice(0,10)}</span>
                    </div>
                  ))}
                  {/* Log contact form */}
                  {showContact && (
                    <div className="bg-white border border-rose-200 rounded-sm p-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <select value={contactForm.method} onChange={e=>setContactForm({...contactForm,method:e.target.value})} className="input text-xs">
                          {['Phone','Email','WhatsApp','Registered Post','Telegram'].map(m=><option key={m}>{m}</option>)}
                        </select>
                        <select value={contactForm.outcome} onChange={e=>setContactForm({...contactForm,outcome:e.target.value})} className="input text-xs">
                          {['No response','Connected','Promised to return','Refused'].map(o=><option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <input value={contactForm.notes} onChange={e=>setContactForm({...contactForm,notes:e.target.value})} placeholder="Notes..." className="input text-xs w-full" />
                      <div className="flex gap-2">
                        <button onClick={logContact} className="text-xs px-2 py-1 bg-rose-700 text-white rounded-sm">Save</button>
                        <button onClick={()=>setShowContact(false)} className="text-xs px-2 py-1 border border-slate-300 rounded-sm">Cancel</button>
                      </div>
                    </div>
                  )}
                  {/* Close case form */}
                  {showAbscClose && (
                    <div className="bg-white border border-rose-200 rounded-sm p-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <select value={abscClose.outcome} onChange={e=>setAbscClose({...abscClose,outcome:e.target.value})} className="input text-xs">
                          <option value="returned_to_duty">Returned to duty (exit cancelled)</option>
                          <option value="terminated_abandonment">Terminated — abandonment</option>
                        </select>
                        <input type="date" value={abscClose.closed_on} onChange={e=>setAbscClose({...abscClose,closed_on:e.target.value})} className="input text-xs" />
                      </div>
                      <input value={abscClose.reason} onChange={e=>setAbscClose({...abscClose,reason:e.target.value})} placeholder="Closure reason..." className="input text-xs w-full" />
                      {abscClose.outcome === 'terminated_abandonment' && (
                        <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5" data-testid="absc-close-fnf-note">
                          On close, EL &amp; CO are always forfeited; only PL is encashed at F&amp;F. No override.
                        </p>
                      )}
                      <div className="flex gap-2">
                        <button onClick={closeAbsconding} className="text-xs px-2 py-1 bg-rose-700 text-white rounded-sm">Confirm Close</button>
                        <button onClick={()=>setShowAbscClose(false)} className="text-xs px-2 py-1 border border-slate-300 rounded-sm">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Exit Checklist */}
              <div className="bg-white border border-slate-200 rounded-sm" data-testid="exit-assets-section">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-display font-bold text-base flex items-center gap-2"><Boxes size={16} /> Company Assets</h3>
                  <span className="text-xs text-slate-500">{empAssets ? `${empAssets.active_count} to recover` : '…'}</span>
                </div>
                {!empAssets ? (
                  <div className="p-5 text-sm text-slate-500">Loading allocated assets…</div>
                ) : empAssets.active_count === 0 ? (
                  <div className="p-5 text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 size={16} /> No company assets pending return. Clear to settle.</div>
                ) : (
                  <div>
                    <div className="px-5 py-2 bg-amber-50 text-amber-800 text-xs flex items-center gap-2 border-b border-amber-100">
                      <AlertTriangle size={14} /> These assets must be returned (or written off) before F&F can be finalised.
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {empAssets.active.map((al) => (
                        <li key={al.id} className="px-5 py-2.5 flex items-center justify-between" data-testid={`exit-asset-${al.id}`}>
                          <span className="text-sm">{al.asset_name} <span className="text-slate-400 text-xs font-mono">{al.asset_tag}</span></span>
                          <span className="text-[10px] text-amber-600 font-bold uppercase">Pending return</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* F&F */}
              <div className="bg-white border border-slate-200 rounded-sm">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-display font-bold text-base">Full & Final Settlement</h3>
                  <button onClick={calcFnf} className="text-xs font-display font-bold uppercase tracking-wider text-[#3b4872] hover:underline flex items-center gap-1" disabled={selected.status === 'Completed'} data-testid="calc-fnf-btn">
                    <Calculator size={12} /> Recalculate
                  </button>
                </div>
                {selected.fnf ? (
                  <div className="p-5 space-y-2">
                    <FnfRow label="Pro-rated Salary" value={selected.fnf.pro_rated_salary} />
                    <FnfRow label={`Leave Encashment (${selected.fnf.leave_balance_days} days)`} value={selected.fnf.leave_encashment} />
                    <FnfRow label="Gratuity" value={selected.fnf.gratuity} />
                    <FnfRow label="PF Balance (estimate)" value={selected.fnf.pf_balance_estimate} />
                    {selected.fnf.notice_recovery > 0 && <FnfRow label={`Notice Shortfall Recovery (${selected.fnf.notice_shortfall_days}d)`} value={-selected.fnf.notice_recovery} negative />}
                    {selected.fnf.advance_recovery > 0 && (
                      <div data-testid="fnf-advance-recovery">
                        <FnfRow label={`Salary Advance Recovery (${(selected.fnf.advance_details || []).map(a => a.advance_no).join(', ') || 'open advances'})`} value={-selected.fnf.advance_recovery} negative />
                      </div>
                    )}
                    {selected.fnf.advance_shortfall > 0 && (
                      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2" data-testid="fnf-advance-shortfall">
                        ₹{Math.round(selected.fnf.advance_shortfall).toLocaleString('en-IN')} of the salary advance cannot be recovered from this settlement —
                        the advance stays open for Finance to pursue or write off.
                      </div>
                    )}
                    <div className="pt-3 mt-3 border-t border-slate-200 flex items-center justify-between">
                      <span className="font-display font-bold text-base">Total Payable</span>
                      <span className="font-display font-extrabold text-2xl text-[#3b4872]">₹ {selected.fnf.total_payable.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-sm text-slate-500">Click <strong>Recalculate</strong> to compute F&F.</div>
                )}
              </div>

              {selected.status !== 'Completed' && (
                <button onClick={complete} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-sm font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2" data-testid="complete-exit-btn">
                  <FileCheck size={16} /> Mark Exit Complete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FnfRow({ label, value, negative }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`font-display font-semibold ${negative ? 'text-red-700' : 'text-[#0F172A]'}`}>
        {negative ? '−' : ''}₹ {Math.abs(value).toLocaleString('en-IN')}
      </span>
    </div>
  );
}
